import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { STATUS_COLOR, STATUS_LABEL } from "../data";
import {
  downloadETaxItemPdf,
  getBatchDetails,
  refreshETax,
} from "../../../services";
import { Dayjs } from "dayjs";
import { ETaxBatchDetail, ETaxBatchItem } from "../../../types/e-tax.type";
import { formatDate } from "../../../utils/date";
import { formatNumber, numberWithCommas } from "../../../utils";
import { AxiosError } from "axios";
import { pushGlobalError } from "../../../errors/globalError";
import {
  ETAX_PDF_DOWNLOAD_ERROR_MESSAGE,
  ETAX_PDF_NOT_FOUND_MESSAGE,
  useEtaxPdfDownload,
} from "../../../utils/hooks/useEtaxPdfDownload";
import { useEtaxSignedPdf } from "../../../utils/hooks/useEtaxSignedPdf";
import { useEtaxWithdraw } from "../../../utils/hooks/useEtaxWithdraw";
import {
  canWithdrawSubmission,
  getKtbDocumentStatusLabel,
  getSignedPdfTooltip,
  getWithdrawTooltip,
} from "../documentMeta";
import CancelSubmissionModal from "../CancelSubmissionModal";
import {
  downloadBlob,
  getFilenameFromContentDisposition,
} from "../../SalesReportExport/utils";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import VerifiedIcon from "@mui/icons-material/Verified";
import SyncIcon from "@mui/icons-material/Sync";
import CancelScheduleSendIcon from "@mui/icons-material/CancelScheduleSend";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";
import {
  SummaryDetailCard,
  ETaxStatusCountSummary,
  DocTypeChip,
  BillStatusDot,
  EtaxStatusBadge,
  LoadErrorState,
  headCellSx,
} from "../documentCells";

const compact = "@media " + COMPACT_QUERY;

interface BatchTableProps {
  selectedBatchId: string;
  triggerFetch: number;
  setTriggerFetch: Dispatch<SetStateAction<number>>;
  /** The history page's range: items outside it are marked, and the counted header total
   * is narrowed to it. Every item is still listed. */
  dateFrom?: Dayjs | null;
  dateTo?: Dayjs | null;
}

const COLUMN_COUNT = 14;

const OUT_OF_RANGE_LABEL = "นอกช่วงวันที่";
const OUT_OF_RANGE_TOOLTIP =
  "วันขายของบิลนี้อยู่นอกช่วงวันที่ที่เลือก จึงไม่รวมในยอดของหน้าประวัติ";
const NOT_COUNTED_LABEL = "ไม่นับยอด";
const NOT_COUNTED_TOOLTIP =
  "ไม่นับเป็นส่งแล้ว (ส่งไม่สำเร็จ / บิลถูกยกเลิก / มีการส่งใหม่ภายหลัง / บิลทดสอบ)";

/** Why an item is left out of the history row's figures, or null when it is counted.
 * An older backend sends neither flag, and every item then reads as counted. */
const getExclusion = (
  item: Pick<ETaxBatchItem, "is_counted" | "in_range">,
): { label: string; tooltip: string } | null => {
  if (item.is_counted === false)
    return { label: NOT_COUNTED_LABEL, tooltip: NOT_COUNTED_TOOLTIP };
  if (item.in_range === false)
    return { label: OUT_OF_RANGE_LABEL, tooltip: OUT_OF_RANGE_TOOLTIP };
  return null;
};

const renderSkeletonRows = () => {
  return Array.from({ length: 10 }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: COLUMN_COUNT }).map((__, colIndex) => (
        <TableCell key={colIndex}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  ));
};

export default function BatchTable({
  selectedBatchId,
  triggerFetch,
  setTriggerFetch,
  dateFrom = null,
  dateTo = null,
}: BatchTableProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [downloadingMany, setDownloadingMany] = useState<boolean>(false);
  const [batchDetails, setBatchDetails] = useState<ETaxBatchDetail>();
  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);

  const { downloadingId, downloadPdf } = useEtaxPdfDownload();
  const { signedPdfBusyId, viewSignedPdf } = useEtaxSignedPdf();
  const isDownloadBusy = downloadingId !== null || downloadingMany;

  // A withdrawal changes the item's status, so the batch has to be re-read afterwards —
  // the same reload the KTB status refresh already uses.
  const reloadBatch = useCallback(
    () => setTriggerFetch((prev) => prev + 1),
    [setTriggerFetch],
  );
  const {
    withdrawTarget,
    withdrawing,
    askWithdraw,
    closeWithdraw,
    confirmWithdraw,
  } = useEtaxWithdraw(reloadBatch);

  const handleSelectRow = (uuid: string) => {
    setSelectedBatchIds((prev) =>
      prev.includes(uuid) ? prev.filter((id) => id !== uuid) : [...prev, uuid],
    );
  };

  const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      // Only items that have a generated PDF (path_file) can be downloaded.
      setSelectedBatchIds(
        batchDetails?.items
          .filter((item) => item.path_file)
          .map((item) => item.etax_uuid) ?? [],
      );
    } else {
      setSelectedBatchIds([]);
    }
  };

  const onGetBatchDetails = useCallback(
    async (batchId: string) => {
      setLoading(true);
      setLoadError(false);
      try {
        const {
          data: { data },
        } = await getBatchDetails(batchId, dateFrom, dateTo);

        setBatchDetails(data);
      } catch {
        // The axios interceptor surfaces the error toast; clear the data so the
        // error state renders instead of a perpetual skeleton.
        setBatchDetails(undefined);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    },
    [dateFrom, dateTo],
  );

  useEffect(() => {
    if (selectedBatchId) {
      onGetBatchDetails(selectedBatchId);
    }
  }, [triggerFetch, selectedBatchId, onGetBatchDetails]);

  const handleRefreshETax = async (uuid: string) => {
    setRefreshing(true);
    try {
      await refreshETax(uuid);
      setTriggerFetch((prev) => prev + 1);
    } catch {
      // Surfaced by the axios interceptor; nothing else to recover here.
    } finally {
      setRefreshing(false);
    }
  };

  const downloadableSelectedItems = batchDetails
    ? batchDetails.items.filter(
        (item) => item.path_file && selectedBatchIds.includes(item.etax_uuid),
      ).length
    : 0;

  const handleDownloadMultipleETaxPdf = async () => {
    if (isDownloadBusy || selectedBatchIds.length === 0) return;
    setDownloadingMany(true);
    try {
      // Only selected items with a generated PDF (path_file) are downloadable.
      const itemsToDownload = (batchDetails?.items ?? []).filter(
        (item) => item.path_file && selectedBatchIds.includes(item.etax_uuid),
      );
      if (itemsToDownload.length === 0) {
        pushGlobalError(ETAX_PDF_DOWNLOAD_ERROR_MESSAGE);
        return;
      }

      // Stream each PDF from the same-origin API and save it. A single failed
      // file is skipped; the error toast only fires if every file failed — and
      // uses the specific "not found" message when every failure was a 404.
      let successCount = 0;
      let onlyNotFound = true;
      for (const item of itemsToDownload) {
        try {
          const response = await downloadETaxItemPdf(item.etax_uuid);
          const blob = response.data as Blob;
          if (
            !(blob instanceof Blob) ||
            blob.size === 0 ||
            blob.type.includes("application/json")
          ) {
            onlyNotFound = false;
            continue;
          }
          const filename =
            getFilenameFromContentDisposition(
              response.headers["content-disposition"] as string | undefined,
            ) || `${item.transaction_no}.pdf`;
          downloadBlob(filename, blob);
          successCount += 1;
        } catch (error) {
          if ((error as AxiosError).response?.status !== 404) onlyNotFound = false;
        }
      }

      if (successCount === 0) {
        pushGlobalError(
          onlyNotFound
            ? ETAX_PDF_NOT_FOUND_MESSAGE
            : ETAX_PDF_DOWNLOAD_ERROR_MESSAGE,
        );
        return;
      }

      setSelectedBatchIds([]);
    } catch {
      pushGlobalError(ETAX_PDF_DOWNLOAD_ERROR_MESSAGE);
    } finally {
      setDownloadingMany(false);
    }
  };

  // Money totals + send-status breakdown for the compact summary strip. The goods
  // value / VAT split is the real per-bill aggregate returned by the backend
  // (sales_report_item.amount_total / .vat_total) with an inclusive-VAT fallback.
  const netTotal = batchDetails ? Number(batchDetails.transaction_grand_total) : 0;
  const beforeVat = batchDetails?.transaction_amount_before_vat ?? 0;
  const vat = batchDetails?.transaction_vat_amount ?? 0;
  const successCount = batchDetails?.transaction_success_count ?? 0;
  const failedCount = batchDetails?.transaction_failure_count ?? 0;
  // Every item in a batch has been submitted — `etax_batch_item.status` is NOT NULL and
  // only ever COMPLETED / IN_PROGRESS / FAILED — so what is left over is still in flight.
  const inProgressCount = batchDetails
    ? Math.max(batchDetails.transaction_count - successCount - failedCount, 0)
    : 0;
  // Counted (ส่งแล้ว) and in range — the figure the history row shows. Absent on an older
  // backend, in which case only the whole-batch figures are shown.
  const hasCountedTotals =
    batchDetails?.counted_transaction_count !== undefined &&
    batchDetails?.counted_transaction_count !== null;
  const hasRange = Boolean(dateFrom?.isValid() || dateTo?.isValid());

  return (
    <>
      <Box sx={{ mb: 2, [compact]: { mb: 1.5 } }}>
        {batchDetails && (
          <>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              [compact]: { flexWrap: "wrap", gap: 1, alignItems: "center" },
            }}
          >
            <Grid
              container
              alignItems="center"
              spacing={1}
              sx={{ width: "32.6%", [compact]: { width: "100%" } }}
            >
              <Grid size={3}>
                <Typography variant="body2" noWrap>
                  Batch Name
                </Typography>
              </Grid>
              <Grid size={9}>
                <TextField
                  size="small"
                  disabled
                  fullWidth
                  value={batchDetails.batch_name}
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#000",
                      color: "#000",
                    },
                  }}
                />
              </Grid>
            </Grid>
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: STATUS_COLOR[batchDetails.status],
                  }}
                />
                <Typography variant="body2">
                  {STATUS_LABEL[batchDetails.status]}
                </Typography>
              </Box>
              <IconButton
                color="primary"
                onClick={() => handleRefreshETax(batchDetails.etax_batch_uuid)}
                disabled={refreshing}
                sx={{ bgcolor: "#fff" }}
              >
                <SyncIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            </Box>
            {downloadableSelectedItems > 0 && (
              <Button
                variant="contained"
                startIcon={
                  downloadingMany ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                disabled={isDownloadBusy}
                onClick={handleDownloadMultipleETaxPdf}
                sx={{ ml: "auto" }}
              >
                ดาวน์โหลดทั้งหมด ({downloadableSelectedItems})
              </Button>
            )}
          </Box>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            mt={2}
            sx={{ [compact]: { flexWrap: "wrap", mt: 1.5 } }}
          >
            <SummaryDetailCard
              title="มูลค่าสินค้า"
              value={`${formatNumber(beforeVat)} บาท`}
            />
            <SummaryDetailCard
              title="ภาษีมูลค่าเพิ่ม (VAT)"
              value={`${formatNumber(vat)} บาท`}
            />
            <SummaryDetailCard
              title="รวมทั้งสิ้น"
              value={`${formatNumber(netTotal)} บาท`}
            />
            <ETaxStatusCountSummary
              count={batchDetails.transaction_count}
              counts={{
                success: successCount,
                in_progress: inProgressCount,
                failed: failedCount,
              }}
            />
          </Box>
          {hasCountedTotals && (
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              mt={1}
              flexWrap="wrap"
            >
              <Typography variant="body2" color="text.secondary">
                {hasRange ? "นับเป็นส่งแล้วในช่วงวันที่ที่เลือก" : "นับเป็นส่งแล้ว"}:{" "}
                <Box component="span" fontWeight={700} color="text.primary">
                  {numberWithCommas(batchDetails.counted_transaction_count ?? 0)} ใบ ·{" "}
                  {formatNumber(Number(batchDetails.counted_grand_total ?? 0))} บาท
                </Box>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ทั้ง batch:{" "}
                <Box component="span" fontWeight={700} color="text.primary">
                  {numberWithCommas(batchDetails.transaction_count)} ใบ ·{" "}
                  {formatNumber(netTotal)} บาท
                </Box>
              </Typography>
            </Box>
          )}
          </>
        )}
      </Box>

      <TableContainer sx={{ borderRadius: "12px" }}>
        <Table>
          <TableHead
            sx={{
              bgcolor: (theme) => theme.palette.background.default,
            }}
          >
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    batchDetails &&
                    selectedBatchIds.length > 0 &&
                    selectedBatchIds.length ===
                      batchDetails.items.filter((item) => item.path_file).length
                  }
                  indeterminate={
                    selectedBatchIds.length > 0 &&
                    selectedBatchIds.length <
                      (batchDetails?.items.filter((item) => item.path_file)
                        .length || 0)
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={headCellSx}>#</TableCell>
              <TableCell sx={headCellSx}>สร้าง</TableCell>
              <TableCell sx={headCellSx}>เลขที่</TableCell>
              <TableCell sx={headCellSx}>Tax ID</TableCell>
              <TableCell sx={headCellSx}>ชื่อลูกค้า</TableCell>
              <TableCell sx={headCellSx}>ประเภท</TableCell>
              <TableCell sx={headCellSx}>สนญ.</TableCell>
              <TableCell align="right" sx={headCellSx}>
                มูลค่าสินค้า
              </TableCell>
              <TableCell align="right" sx={headCellSx}>
                ภาษีมูลค่าเพิ่ม (VAT)
              </TableCell>
              <TableCell align="right" sx={headCellSx}>
                รวมทั้งสิ้น
              </TableCell>
              <TableCell sx={headCellSx}>สถานะบิล</TableCell>
              <TableCell sx={headCellSx}>สถานะ</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && renderSkeletonRows()}
            {!loading && loadError && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <LoadErrorState
                    onRetry={() => onGetBatchDetails(selectedBatchId)}
                  />
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              !loadError &&
              (!batchDetails || batchDetails.items.length === 0) && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูล
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              batchDetails &&
              batchDetails.items.map((transaction, index) => {
                const canDownload = Boolean(transaction.path_file);
                const isDownloading =
                  downloadingId !== null &&
                  downloadingId === transaction.etax_uuid;
                const isViewingSigned =
                  signedPdfBusyId !== null &&
                  signedPdfBusyId === transaction.etax_uuid;
                // Shown (owner decision Q4: the detail lists the whole batch) but muted, so
                // the rows that make up the history row's figures stand out.
                const exclusion = getExclusion(transaction);
                return (
                  <TableRow
                    key={transaction.transaction_uuid}
                    sx={{
                      "& td, & th": { padding: "8px" },
                      bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                      ...(exclusion && {
                        "& td": { padding: "8px", color: "text.disabled" },
                      }),
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedBatchIds.includes(
                          transaction.etax_uuid,
                        )}
                        disabled={!canDownload}
                        onChange={() => handleSelectRow(transaction.etax_uuid)}
                      />
                    </TableCell>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {formatDate(transaction.transaction_date)}
                    </TableCell>
                    <TableCell>
                      {transaction.transaction_no}
                      {exclusion && (
                        <Tooltip arrow title={exclusion.tooltip}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            noWrap
                            sx={{ fontStyle: "italic" }}
                          >
                            {exclusion.label}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{transaction.tax_id ?? "-"}</TableCell>
                    <TableCell>{transaction.customer_name}</TableCell>
                    <TableCell>
                      {transaction.document_type ? (
                        <DocTypeChip type={transaction.document_type} />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{transaction.branch_code ?? "-"}</TableCell>
                    <TableCell align="right">
                      {formatNumber(transaction.amount_before_vat)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(transaction.vat_amount)}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(transaction.grand_total)}
                    </TableCell>
                    <TableCell>
                      <BillStatusDot status={transaction.bill_status} />
                    </TableCell>
                    <TableCell>
                      <EtaxStatusBadge status={transaction.status} />
                      {transaction.document_status && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          noWrap
                        >
                          {getKtbDocumentStatusLabel(transaction.document_status)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <IconButton
                          size="small"
                          color="info"
                          sx={{ p: 1.5, gap: 1 }}
                          disabled={!canDownload || isDownloadBusy}
                          onClick={() =>
                            downloadPdf(
                              transaction.etax_uuid,
                              `${transaction.transaction_no}.pdf`,
                            )
                          }
                        >
                          {isDownloading ? (
                            <CircularProgress size={18} />
                          ) : (
                            <DownloadIcon fontSize="small" />
                          )}
                          <Typography variant="subtitle2">ดาวน์โหลด</Typography>
                        </IconButton>
                        <Tooltip
                          title={getSignedPdfTooltip(transaction.document_status)}
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={
                                !transaction.has_signed_document ||
                                signedPdfBusyId !== null
                              }
                              onClick={() =>
                                viewSignedPdf(transaction.etax_uuid)
                              }
                            >
                              {isViewingSigned ? (
                                <CircularProgress size={18} />
                              ) : (
                                <VerifiedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {/* This is the screen someone lands on straight after a send —
                            "that one was wrong" is noticed here — so the withdraw action
                            belongs beside the document it acts on. */}
                        <Tooltip
                          title={getWithdrawTooltip(
                            transaction.status,
                            transaction.document_status,
                          )}
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={
                                !canWithdrawSubmission(
                                  transaction.status,
                                  transaction.document_status,
                                )
                              }
                              onClick={() =>
                                askWithdraw({
                                  transactionUuid: transaction.transaction_uuid,
                                  transactionNo: transaction.transaction_no,
                                  customerName: transaction.customer_name,
                                  documentStatus: transaction.document_status,
                                })
                              }
                            >
                              <CancelScheduleSendIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <CancelSubmissionModal
        target={withdrawTarget}
        loading={withdrawing}
        onCancel={closeWithdraw}
        onConfirm={confirmWithdraw}
      />
    </>
  );
}
